<script setup lang="ts">
import { computed } from 'vue';
import { cn } from '../../design-system/utils';

interface Props {
  name: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const props = withDefaults(defineProps<Props>(), { size: 'md' });

const initials = computed(() => {
  const parts = props.name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
});

// Couleur déterministe selon le nom (hash simple)
const colorIndex = computed(() => {
  let hash = 0;
  for (let i = 0; i < props.name.length; i++) {
    hash = props.name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 5;
});

const palettes = [
  'bg-accent/15 text-accent',
  'bg-info-bg text-info',
  'bg-success-bg text-success',
  'bg-warning-bg text-warning',
  'bg-bg-muted text-text-secondary',
];

const sizeClasses = {
  sm: 'h-7 w-7 text-xs',
  md: 'h-9 w-9 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
};

const classes = computed(() =>
  cn(
    'inline-flex items-center justify-center rounded-full font-semibold select-none overflow-hidden',
    sizeClasses[props.size],
    !props.src && palettes[colorIndex.value],
  ),
);
</script>

<template>
  <span :class="classes" :aria-label="name">
    <img
      v-if="src"
      :src="src"
      :alt="name"
      class="h-full w-full object-cover"
      loading="lazy"
    />
    <span v-else aria-hidden="true">{{ initials }}</span>
  </span>
</template>
